// Queueing analysis inputs and results for the What-If scenario (FBPM Ch.7 §7.2).
//
// Queueing treats a job as one resource pool, not one row per assignment: if the
// Marketing Analyst is Responsible on three tasks, every case puts all three
// stretches of work through the same person. So the tab aggregates the scenario's
// slots by job and sums their attributed minutes into a single service time per
// case, which is exactly the quantity the M/M/c arithmetic needs.
//
// The service minutes are taken from the live scenario rather than the payload,
// so an eliminated task or a reassigned role is reflected here without a round
// trip — see `buildQueueingRequest` for what actually crosses the wire.

import { ROLE_LABELS } from "./whatIfNormalize";

const MINUTES_PER_HOUR = 60;

export const DEFAULT_STAFF_COUNT = 1;

// Utilization bands. Anything at or over 100% is unstable: more work arrives each
// week than the staffing can clear, so the queue grows without bound and a wait
// time cannot be quoted at all.
export const UNSTABLE_UTILIZATION = 100;
export const STRAINED_UTILIZATION = 85;

export const utilizationBand = (percent, stable) => {
  if (stable === false || percent >= UNSTABLE_UTILIZATION) return "unstable";
  if (percent >= STRAINED_UTILIZATION) return "strained";
  return "healthy";
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// An untouched row shows the headcount the payload reported for that job; once
// the user types, their number wins. An empty string is a deliberate mid-edit
// state and must not fall back to the payload, or the box could not be cleared.
export const readStaffCount = (staffByJob, jobId, recorded) => {
  const raw = staffByJob?.[String(jobId)];
  if (raw == null) return recorded ?? DEFAULT_STAFF_COUNT;
  return raw;
};

// A blank box is still mid-edit, so it counts as one server rather than zero —
// zero staff would divide by nothing and report every job as unstable while the
// user is simply retyping the number.
export const effectiveStaff = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_STAFF_COUNT;
};

// One row per job, carrying the task/role breakdown that produced its service
// time so the user can see why a job is loaded the way it is.
export function deriveQueueJobs(scenario) {
  const byJob = new Map();

  scenario.tasks.forEach((entry) => {
    // An eliminated task stops happening, so its labour leaves the queue too.
    if (entry.eliminated) return;

    entry.slots.forEach(({ slot, minutes, unassigned }) => {
      if (unassigned || slot.jobId == null || slot.jobId === "") return;

      const key = String(slot.jobId);
      const row = byJob.get(key) || {
        key,
        jobId: slot.jobId,
        jobName: slot.jobName,
        serviceMinutesPerCase: 0,
        // The payload states a headcount per assignment. It describes the job,
        // so every assignment of it should agree; the largest wins if they ever
        // disagree, since under-counting servers overstates the queue.
        recordedStaffCount: DEFAULT_STAFF_COUNT,
        assignments: [],
      };

      row.recordedStaffCount = Math.max(
        row.recordedStaffCount,
        toNumber(slot.staffCount, DEFAULT_STAFF_COUNT),
      );
      row.serviceMinutesPerCase += toNumber(minutes);
      row.assignments.push({
        taskId: entry.task.taskId,
        taskName: entry.task.taskName,
        role: slot.role,
        roleLabel: ROLE_LABELS[slot.role],
        minutes: toNumber(minutes),
      });

      byJob.set(key, row);
    });
  });

  // Busiest first: the job most likely to be the bottleneck is the one worth
  // reading, and it is the one whose staff count the user will want to raise.
  return [...byJob.values()].sort(
    (a, b) => b.serviceMinutesPerCase - a.serviceMinutesPerCase,
  );
}

// Every duration on this endpoint is in hours, in both directions — weekly
// capacity is quoted in hours anyway, so hours is the one unit that needs no
// conversion at either end of the queueing arithmetic. The view model stays in
// minutes like the rest of the What-If page, so the boundary converts and
// nothing downstream has to know.
const toWireHours = (minutes) =>
  Math.round((toNumber(minutes) / MINUTES_PER_HOUR) * 10000) / 10000;

const fromWireHours = (hours) => toNumber(hours) * MINUTES_PER_HOUR;

// What crosses the wire. The scenario's own arithmetic is sent as
// `service_hours_per_case` rather than re-derived server-side, so the queueing
// figures cannot drift from the effort and cost the other two tabs already show.
// `eliminated_task_ids` travels along because it explains any drop against the
// as-is analysis the backend holds.
export function buildQueueingRequest({ arrivalPerWeek, jobs, staffByJob, scenario }) {
  return {
    arrival_rate_cases_per_week: toNumber(arrivalPerWeek),
    jobs: jobs.map((job) => ({
      job_id: job.jobId,
      job_name: job.jobName,
      staff_count: effectiveStaff(
        readStaffCount(staffByJob, job.jobId, job.recordedStaffCount),
      ),
      service_hours_per_case: toWireHours(job.serviceMinutesPerCase),
    })),
    eliminated_task_ids: scenario.tasks
      .filter((entry) => entry.eliminated)
      .map((entry) => entry.task.taskId),
  };
}

const normalizeQueueJob = (job) => ({
  jobId: job?.job_id ?? job?.jobId ?? null,
  jobName: String(job?.job_name ?? job?.jobName ?? "").trim(),
  staffCount: toNumber(job?.staff_count ?? job?.staffCount, DEFAULT_STAFF_COUNT),
  // Back to minutes on the way in, so every duration in the view model shares one
  // base and the page's minutes/hours selector keeps working unchanged.
  serviceMinutesPerCase: fromWireHours(
    job?.service_hours_per_case ?? job?.serviceHoursPerCase,
  ),
  // Weekly figures stay in hours: they are a rate per week, not a per-case
  // duration, so the page's unit selector does not apply to them.
  weeklyWorkloadHours: toNumber(
    job?.weekly_workload_hours ?? job?.weeklyWorkloadHours,
  ),
  weeklyCapacityHours: toNumber(
    job?.weekly_capacity_hours ?? job?.weeklyCapacityHours,
  ),
  utilizationPercent: toNumber(job?.utilization_pct ?? job?.utilizationPercent),
  // Absent means "the backend did not say", which is not the same as unstable —
  // fall back to the utilization it did report.
  stable:
    job?.stable ??
    toNumber(job?.utilization_pct ?? job?.utilizationPercent) <
      UNSTABLE_UTILIZATION,
  // Unbounded queues have no wait to quote, so null has to survive as null
  // rather than converting into a very confident zero.
  queueWaitMinutesPerCase:
    job?.queue_wait_hours_per_case == null &&
    job?.queueWaitHoursPerCase == null
      ? null
      : fromWireHours(
          job?.queue_wait_hours_per_case ?? job?.queueWaitHoursPerCase,
        ),
});

export function normalizeQueueingResponse(payload) {
  const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
  const bottleneck = payload?.bottleneck_job ?? payload?.bottleneckJob ?? null;

  return {
    status: String(payload?.status ?? "ok"),
    arrivalPerWeek: toNumber(
      payload?.arrival_rate_cases_per_week ?? payload?.arrivalPerWeek,
    ),
    bottleneckJobId: bottleneck?.job_id ?? bottleneck?.jobId ?? null,
    // Keyed rather than listed: the table's row order is the scenario's, so every
    // read of a result is a lookup by job. A parallel array would only be a
    // second copy to keep in step.
    byJobId: jobs.reduce((result, job) => {
      const normalized = normalizeQueueJob(job);
      if (normalized.jobId != null) {
        result[String(normalized.jobId)] = normalized;
      }
      return result;
    }, {}),
  };
}

// Totals worth stating once rather than making the user scan the column: the
// whole-case service time, and how close the worst-off job is to saturation.
export function summarizeQueueing(jobs, result) {
  const serviceMinutesPerCase = jobs.reduce(
    (sum, job) => sum + job.serviceMinutesPerCase,
    0,
  );

  const computed = result ? Object.values(result.byJobId) : [];
  const unstable = computed.filter(
    (job) => utilizationBand(job.utilizationPercent, job.stable) === "unstable",
  );

  return {
    jobCount: jobs.length,
    serviceMinutesPerCase,
    peakUtilization: computed.reduce(
      (peak, job) => Math.max(peak, job.utilizationPercent),
      0,
    ),
    unstableCount: unstable.length,
  };
}
